import { Controller } from "@hotwired/stimulus"

/** Sizes prompt copy up to a cap so it fits inside this element (flex box including actions). */
export default class extends Controller {
  static targets = ["text"]
  static values = {
    maxPx: { type: Number, default: 0 },
    minPx: { type: Number, default: 11 },
    iterations: { type: Number, default: 26 },
    /** Applied after fitting — fraction of largest size that still fits (lower → smaller type). */
    sizeScale: { type: Number, default: 0.65 },
  }

  connect() {
    this._resizeObserver = new ResizeObserver(() => this.scheduleFit())
    this._resizeObserver.observe(this.element)
    this.scheduleFit()
  }

  disconnect() {
    this._resizeObserver?.disconnect()
    if (this._raf != null) cancelAnimationFrame(this._raf)
  }

  scheduleFit() {
    if (this._raf != null) cancelAnimationFrame(this._raf)
    this._raf = requestAnimationFrame(() => {
      this._raf = null
      this.fit()
      requestAnimationFrame(() => this.fit())
    })
  }

  overflow(box) {
    return (
      box.scrollHeight > box.clientHeight + 1 ||
      box.scrollWidth > box.clientWidth + 1
    )
  }

  capHi(box) {
    if (this.maxPxValue > 0) return this.maxPxValue
    const w = box.clientWidth
    const h = box.clientHeight
    /* Slightly under old width×0.42 so “max that fits” isn’t pushing slab-serif to the edges. */
    return Math.min(240, Math.max(w * 0.34, h * 0.24))
  }

  applyFinalSize(text, fittedPx) {
    const scaled = fittedPx * this.sizeScaleValue
    text.style.fontSize = `${Math.max(8, scaled)}px`
  }

  fit() {
    const box = this.element
    const text = this.hasTextTarget ? this.textTarget : null
    if (!text || box.clientHeight < 4) return

    const minPx = Math.max(8, this.minPxValue)
    const hiCap = Math.max(minPx + 0.5, this.capHi(box))

    text.style.fontSize = `${hiCap}px`
    if (!this.overflow(box)) {
      this.applyFinalSize(text, hiCap)
      return
    }

    text.style.fontSize = `${minPx}px`
    if (this.overflow(box)) return

    let lo = minPx
    let hi = hiCap
    const n = Math.max(8, this.iterationsValue)
    for (let i = 0; i < n; i++) {
      const mid = (lo + hi) / 2
      text.style.fontSize = `${mid}px`
      if (this.overflow(box)) hi = mid
      else lo = mid
    }

    this.applyFinalSize(text, lo)
  }
}
